import { Program, ProgramFormData } from "../types/program";
import { appApi } from "../../../store/app.api";
import { appEndpoint } from "../../../store/app.endpoint";
import { CommandRequest } from "../../../libs/auth/utilities/command-request";

/** ---------- Helper to build FormData ---------- */
function buildProgramFormData(data: ProgramFormData): FormData {
  const formData = new FormData();

  formData.append("name", data.name);
  formData.append("description", data.description);

  // ✅ Nested objectives
  data.objectives.forEach((obj, index) => {
    formData.append(`objectives[${index}][program_objective]`, obj);
  });

  // ✅ Nested achievements
  data.achievements.forEach((ach, index) => {
    formData.append(`achievements[${index}][program_achievement]`, ach);
  });

  // ✅ Category IDs
  data.category_ids.forEach((id, index) => {
    formData.append(`program_categories[${index}]`, id);
  });

  // ✅ Image (optional)
  if (data.image) {
    formData.append("image", data.image);
  }

  return formData;
}

export const programsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    /** ✅ Fetch all programs */
    getPrograms: build.query<Program[], void>({
      query: () => ({
        url: appEndpoint.getPrograms,
        method: "get",
      }),
      providesTags: ["Programs"],
      transformResponse: (response: any) => response.data,
    }),

    /** ✅ Fetch a single program by ID */
    getProgramById: build.query<Program, string>({
      query: (id) => ({
        url: `${appEndpoint.getPrograms}/${id}`,
        method: "get",
      }),
      providesTags: (result, error, id) => [{ type: "Programs", id }],
      transformResponse: (response: any) => response.data,
    }),

    /** ✅ Add new program (with image upload support) */
    addProgram: build.mutation<any, ProgramFormData>({
      query: (data) => ({
        url: appEndpoint.addProgram,
        method: "post",
        data: buildProgramFormData(data),
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Programs"],
    }),

    /** ✅ Update existing program (with image upload support) */
    updateProgram: build.mutation<
      any,
      { id: string; formData: ProgramFormData }
    >({
      query: ({ id, formData }) => ({
        url: `${appEndpoint.updateProgram}/${id}?_method=put`,
        method: "post",
        data: buildProgramFormData(formData),
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["Programs"],
    }),

    /** ✅ Delete program */
    deleteProgram: build.mutation<void, string>({
      query: (id) => ({
        url: `${appEndpoint.deleteProgram}/${id}`,
        method: "delete",
      }),
      invalidatesTags: ["Programs"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProgramsQuery,
  useGetProgramByIdQuery,
  useAddProgramMutation,
  useUpdateProgramMutation,
  useDeleteProgramMutation,
} = programsApi;
